// Shared sortable / searchable / paginated table.
//
// Replaces the react-table based `examples/Tables/DataTable` used by the
// casino_dashboard project with a plain MUI table styled with the tokens from
// src/theme.js, so the ported pages match the rest of this admin.
//
// Columns: { header, accessor, align, width, render(value, row), sortable, searchable }
// Rows:    plain objects keyed by accessor — keep raw (string/number) values so
//          search and sort work; use `render` for chips, links, etc.
import React from "react";
import {
  Box,
  InputBase,
  MenuItem,
  Select,
  TableSortLabel,
  Typography,
  TablePagination,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { tokens } from "../theme";
import { th, td, TablePaginationActions } from "./tableKit";

// "$1,234.50" / "12.5%" / "3x" → 1234.5 / 12.5 / 3 so numeric columns sort
// numerically instead of lexicographically.
const asNumber = (v) => {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return NaN;
  const cleaned = v.replace(/[$,%\s]/g, "").replace(/x$/i, "");
  if (cleaned === "" || Number.isNaN(Number(cleaned))) return NaN;
  return Number(cleaned);
};

const compare = (a, b) => {
  const na = asNumber(a);
  const nb = asNumber(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true });
};

const cellText = (value) => {
  if (value == null) return "";
  if (typeof value === "object") return ""; // React node — not searchable
  return String(value);
};

export default function DataTable({
  columns: allColumns = [],
  rows = [],
  searchable = false,
  sortable = false,
  paginated = false,
  entriesPerPage = false,
  showTotalEntries = false,
  defaultRowsPerPage = 10,
  emptyText = "No records found.",
  searchPlaceholder = "Search…",
  rowKey,
  loading = false,
}) {
  // `hidden` columns stay out of the markup but are still matched by search,
  // so a page can keep e.g. email searchable without giving it a column.
  const columns = React.useMemo(() => allColumns.filter((c) => !c.hidden), [allColumns]);

  const [query, setQuery] = React.useState("");
  const [orderBy, setOrderBy] = React.useState(null);
  const [order, setOrder] = React.useState("asc");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);

  // Any change to the underlying data or the filter invalidates the page index.
  React.useEffect(() => {
    setPage(0);
  }, [query, rows]);

  const searchCols = React.useMemo(
    () => allColumns.filter((c) => c.searchable !== false),
    [allColumns]
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      searchCols.some((c) => cellText(r[c.accessor]).toLowerCase().includes(q))
    );
  }, [rows, query, searchCols]);

  const sorted = React.useMemo(() => {
    if (!orderBy) return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const res = compare(a[orderBy], b[orderBy]);
      return order === "asc" ? res : -res;
    });
    return copy;
  }, [filtered, orderBy, order]);

  const visible = paginated
    ? sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sorted;

  const handleSort = (accessor) => {
    if (orderBy === accessor) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(accessor);
      setOrder("asc");
    }
  };

  const showToolbar = searchable || entriesPerPage;

  return (
    <Box>
      {showToolbar && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            px: 1.5,
            pb: 1.5,
          }}
        >
          {entriesPerPage ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                sx={{ fontSize: 12.5, height: 32, minWidth: 74 }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: 12.5 }}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary">
                entries per page
              </Typography>
            </Box>
          ) : (
            <Box />
          )}

          {searchable && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.2,
                height: 32,
                borderRadius: "8px",
                background: tokens.bg,
                border: `1px solid ${tokens.border}`,
                minWidth: 220,
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 16, color: tokens.textDim }} />
              <InputBase
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ color: tokens.text, fontSize: 12.5, flex: 1 }}
              />
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {columns.map((c) => {
                const canSort = sortable && c.sortable !== false;
                return (
                  <th
                    key={c.accessor}
                    style={{ ...th, textAlign: c.align || "left", width: c.width }}
                  >
                    {canSort ? (
                      <TableSortLabel
                        active={orderBy === c.accessor}
                        direction={orderBy === c.accessor ? order : "asc"}
                        onClick={() => handleSort(c.accessor)}
                        sx={{
                          color: "inherit !important",
                          "& .MuiTableSortLabel-icon": { color: `${tokens.textDim} !important` },
                        }}
                      >
                        {c.header}
                      </TableSortLabel>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td style={{ ...td, textAlign: "center", color: tokens.textDim }} colSpan={columns.length}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && visible.length === 0 && (
              <tr>
                <td style={{ ...td, textAlign: "center", color: tokens.textDim }} colSpan={columns.length}>
                  {emptyText}
                </td>
              </tr>
            )}
            {!loading &&
              visible.map((row, i) => (
                <tr key={rowKey ? rowKey(row, i) : i}>
                  {columns.map((c) => (
                    <td
                      key={c.accessor}
                      style={{ ...td, textAlign: c.align || "left" }}
                    >
                      {c.render ? c.render(row[c.accessor], row) : row[c.accessor]}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </Box>
      </Box>

      {paginated && sorted.length > 0 && (
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={entriesPerPage ? [] : [10, 25, 50, 100]}
          labelDisplayedRows={({ from, to, count }) =>
            showTotalEntries ? `Showing ${from}–${to} of ${count} entries` : `${from}–${to} of ${count}`
          }
          ActionsComponent={TablePaginationActions}
          sx={{ color: tokens.textDim, borderTop: `1px solid ${tokens.border}` }}
        />
      )}
    </Box>
  );
}
